import "./Tools.scss";

import { useState } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useLocalStorage } from "usehooks-ts";

import { useUser } from "../../../../contexts/userContext";
import type { ToolsType, ToolType } from "../../types";
import { ToolItem } from "./ToolItem";
import { ToolsModal } from "./ToolsModal";

interface ToolsProps {
  selectedTools: ToolsType;
  setSelectedTools: (tools: ToolsType) => void;
}

const Tools: React.FC<ToolsProps> = ({ selectedTools, setSelectedTools }) => {
  const [show, setShow] = useState(false);
  const [selectedTool, setSelectedTool] = useState<ToolType | null>(null);
  const [toolsFromStore, setTools] = useLocalStorage<Record<string, ToolsType>>(
    "tools",
    {},
  );

  const { user } = useUser();

  const toggleModal = () => setShow(!show);

  const handleEdit = (tool: ToolType) => {
    setSelectedTool(tool);
    toggleModal();
  };

  const handleDelete = (id: number) => {
    const tools = { ...selectedTools };
    delete tools[id];
    setSelectedTools(tools);
  };

  const handleDeleteToolFromStore = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number,
  ) => {
    e.stopPropagation();
    const tools = { ...selectedTools };
    delete tools[id];
    setSelectedTools(tools);

    if (user?.sub) {
      delete toolsFromStore[user?.sub][id];
      setTools(toolsFromStore);
    }
  };

  const handleSaveTool = (tool: ToolType) => {
    if (!user?.sub) return;

    const userTools = toolsFromStore[user?.sub] || {};

    const existedInUserTools = Object.values(userTools).find((t: ToolType) => {
      return t.tool.function.name === tool.tool.function.name;
    });

    const existedInSelectedTools = Object.values(selectedTools).find(
      (t: ToolType) => {
        return t.tool.function.name === tool.tool.function.name;
      },
    );

    if (existedInUserTools) {
      if (existedInSelectedTools) {
        toast.error("Function should be unique");
        return;
      }
      setSelectedTools({
        ...selectedTools,
        [tool.id]: tool,
      });
      toggleModal();
      return;
    }
    setSelectedTools({
      ...selectedTools,
      [tool.id]: tool,
    });
    setTools(prev => ({
      ...prev,
      [user.sub]: {
        ...prev[user.sub],
        [tool.id]: tool,
      },
    }));
    toggleModal();
  };

  const toolsList = Object.values(selectedTools);

  return (
    <>
      <div className="playground-controls-item">
        <label>Tools</label>
        {toolsList.map(tool => (
          <ToolItem
            key={tool.id}
            tool={tool}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        ))}
        <div>
          <Button
            variant="primary"
            size="lg"
            className="w-100"
            onClick={toggleModal}
          >
            + Add tool
          </Button>
        </div>
      </div>
      {show && (
        <ToolsModal
          selectedTool={selectedTool}
          handleClose={toggleModal}
          handleSaveTool={handleSaveTool}
          changeSelectedTool={setSelectedTool}
          handleDeleteToolFromStore={handleDeleteToolFromStore}
          toolsFromStore={toolsFromStore[user?.sub || ""] || {}}
        />
      )}
    </>
  );
};

export { Tools };
